/**
 * The **pilot bridge** — Dismount/Eject/Mount, the three
 * actions that cross the mech ↔ on-foot-pilot boundary (RAW Core p.70/74). Pure rules (no I/O,
 * no dice — all three are fully deterministic once their target cell/mech is chosen): the
 * legality checks + the minted pilot combatant + the recorded step lists the room composes into
 * a batch (`add_combatant`/`apply_resolved`/`stamp_activated`/`spend_action`/`end_turn`), mirroring
 * `shutdown.ts`'s "thin step-builder the room composes with the action-economy spend" shape.
 *
 * **Turn-ending:** Dismount/Eject END the mech's turn (its own separate, independent activation
 * is spent). Mount also folds `end_turn` — not because RAW says so, but because the pilot
 * combatant ceases to exist mid-turn otherwise (see `resolveMount` in `encounterRoom.ts`).
 *
 * **The double-activation guard:** Dismount/Eject spawn the pilot ALREADY stamped into
 * `activation.activated` for the round (`stamp_activated`, room-folded); Mount stamps the mech
 * the same way as it clears Unpiloted. Without this, dismount → a fresh pilot turn the SAME
 * round is a same-round double activation for the pair — the exploit this closes.
 */

import type { Cell, Combatant, CombatantBase, CombatantId, EncounterState, ResolvedStep } from "@lancer-vtt/domain";
import { BIOLOGICAL_STATUS, EJECT_LOCKOUT_RESOURCE, UNPILOTED_STATUS, hasStatus, placementFits, worldFor } from "@lancer-vtt/domain";
import { ok, err, type Result } from "@lancer-vtt/shared";
import { combatantCellRange, combatantRange } from "./effects/spatialAnswerer";
import { coreActionIdsFor } from "./coreActionCatalog";
import { sidearmFightAllowance } from "./weaponProfile";
import { destroyedConversionKind } from "./wreckConversion";

/** A pilot-bridge legality verdict — `Result<void>`, mirroring `economy.ts`'s `BudgetVerdict`. */
export type BridgeVerdict = Result<void>;

/**
 * The deterministic on-foot pilot combatant id for a mech's bridge-spawned pilot — stable, no
 * `crypto.randomUUID` (mirrors `wreckConversion.ts`'s `wreckIdFor`). Reused across repeated
 * dismount/mount/eject cycles on the SAME mech: each cycle fully removes the prior pilot
 * combatant (Mount's `remove_combatant`) before a later Dismount/Eject could mint another one
 * under the same id, so there is no collision.
 */
export function dismountedPilotIdFor(mechId: CombatantId): CombatantId {
  return `${mechId}:pilot`;
}

/**
 * The pilot-size footprint-fit + occupancy check shared by Dismount/Eject: does a pilot
 * (Size 0.5) fit at `at`, with every existing placed combatant (the dismounting mech included)
 * treated as solid? Uses `movementContext`'s missing-mover fallback (an id that can't resolve to
 * a live combatant) — the correct "brand-new spawn, nothing to exempt" semantics, the same one
 * `wreckConversion.ts`'s callers rely on implicitly by constructing `pos` directly. Exported
 * for `jockey.ts`'s `checkRideDismountLegality` — the explicit
 * allied-DISMOUNT reuses this SAME Size-0.5 placement gate rather than a second copy, since a
 * dismounting rider is exactly the same shape check as the mech-bridge's own Dismount/Eject.
 */
export function pilotFootprintFits(state: EncounterState, at: Cell): boolean {
  return placementFits(state, { mover: "__pilot_bridge_spawn__", size: 0.5, anchor: at });
}

/**
 * The destroy-consequence auto-spawn's placement pick: the first neighbor
 * of `deadAnchor` (a dead mech's former footprint) with a free Size-0.5 fit, in the world's
 * own deterministic neighbor order. Falls back to `deadAnchor` itself only when every
 * neighbor is occupied — see {@link resolvePilotEjections}'s doc for why the wreck's own
 * cell is a last resort, not the default (unlike Dismount/Eject's player-chosen placement,
 * which REJECTS rather than falling back — an automated consequence can't reject).
 */
function ejectionCellFor(state: EncounterState, deadAnchor: Cell): Cell {
  const world = worldFor(state);
  for (const n of world.neighbors(deadAnchor)) {
    if (world.inBounds(n) && pilotFootprintFits(state, n)) return n;
  }
  return deadAnchor;
}

/**
 * The status SAFE_CRACKER's Jam Cockpit invade option plants (`t_hacker:2`): "Characters may not
 * MOUNT or DISMOUNT your target until the cockpit is fixed." Read by Mount, Dismount and the allied
 * Ride and Ride-dismount (`jockey.ts`); Eject stays legal (ruling 4, `ll1-invade-options`).
 */
export const JAM_COCKPIT_STATUS = "Jam Cockpit";

/** `verb`'s refusal when `mech`'s cockpit is jammed, else `undefined`. */
export function jammedCockpitRefusal(mech: Combatant, verb: string): string | undefined {
  return hasStatus(mech.statuses, JAM_COCKPIT_STATUS) ? `${verb}: ${mech.name}'s cockpit is jammed — no one can mount or dismount it until it's fixed` : undefined;
}

/**
 * May `mechId` Dismount its pilot to `at` (RAW p.70, `act_dismount`, a FULL action)? Checks, in
 * order: the mech exists/is a mech/carries a frozen `pilotBlock` (was deployed via `spawnMech`)/
 * isn't already Unpiloted/is placed on the map; `at` is exactly adjacent (distance 1) to the
 * mech's footprint; `at` has a free Size-0.5 space (RAW: "reject if none").
 */
export function checkDismountLegality(state: EncounterState, mechId: CombatantId, at: Cell): BridgeVerdict {
  const mech = state.combatants[mechId];
  if (!mech) return err(`dismount: no such combatant ${mechId}`);
  if (mech.kind !== "mech") return err(`dismount: ${mech.name} is not a mech`);
  if (!mech.base.pilotBlock) return err(`dismount: ${mech.name} carries no pilot data (not deployed via spawnMech)`);
  if (hasStatus(mech.statuses, UNPILOTED_STATUS)) return err(`dismount: ${mech.name} is already Unpiloted`);
  const jammed = jammedCockpitRefusal(mech, "dismount");
  if (jammed) return err(jammed);
  if (!mech.pos) return err(`dismount: ${mech.name} isn't placed on the map`);
  const dist = combatantCellRange(state, mechId, at);
  if (dist !== 1) return err(`dismount: target must be an adjacent free space (got distance ${dist ?? "unreachable"})`);
  if (!pilotFootprintFits(state, at)) return err(`dismount: no free adjacent space at that cell`);
  return ok(undefined);
}

/**
 * May `mechId` Eject its pilot to `at` (RAW p.70, `act_eject`, a QUICK action)? Same base checks
 * as Dismount, PLUS: Eject isn't already locked out (`EJECT_LOCKOUT_RESOURCE`, cleared
 * only by Full Repair). `at` may be anywhere within fly-6 of the mech (RAW: "any direction"), not
 * just adjacent.
 */
export function checkEjectLegality(state: EncounterState, mechId: CombatantId, at: Cell): BridgeVerdict {
  const mech = state.combatants[mechId];
  if (!mech) return err(`eject: no such combatant ${mechId}`);
  if (mech.kind !== "mech") return err(`eject: ${mech.name} is not a mech`);
  if (!mech.base.pilotBlock) return err(`eject: ${mech.name} carries no pilot data (not deployed via spawnMech)`);
  if (hasStatus(mech.statuses, UNPILOTED_STATUS)) return err(`eject: ${mech.name} is already Unpiloted`);
  if ((mech.current.resources?.[EJECT_LOCKOUT_RESOURCE] ?? 0) >= 1) {
    return err(`eject: ${mech.name}'s Eject is locked out until Full Repair (RAW p.70)`);
  }
  if (!mech.pos) return err(`eject: ${mech.name} isn't placed on the map`);
  const dist = combatantCellRange(state, mechId, at);
  if (dist === null || dist < 1 || dist > 6) return err(`eject: target must be within fly 6 of ${mech.name} (got distance ${dist ?? "unreachable"})`);
  if (!pilotFootprintFits(state, at)) return err(`eject: no free space at that cell`);
  return ok(undefined);
}

/**
 * May `pilotId` Mount `mechId` (RAW p.70, `act_mount`, the pilot's own FULL action)? The mech
 * must be Unpiloted with its partner pointing at exactly THIS pilot ("its own mech" — the
 * partner-pointer relationship Dismount/Eject stamped, mirroring the Grapple `partner`
 * precedent), and the two must be adjacent (distance 1).
 */
export function checkMountLegality(state: EncounterState, pilotId: CombatantId, mechId: CombatantId): BridgeVerdict {
  const pilot = state.combatants[pilotId];
  if (!pilot) return err(`mount: no such combatant ${pilotId}`);
  if (pilot.kind !== "pilot") return err(`mount: ${pilot.name} is not a pilot`);
  const mech = state.combatants[mechId];
  if (!mech) return err(`mount: no such combatant ${mechId}`);
  if (mech.kind !== "mech") return err(`mount: ${mech.name} is not a mech`);
  const unpiloted = mech.statuses.find((s) => s.name === UNPILOTED_STATUS);
  if (!unpiloted) return err(`mount: ${mech.name} is not Unpiloted`);
  if (unpiloted.partner !== pilotId) return err(`mount: ${mech.name} isn't ${pilot.name}'s own mech`);
  const jammed = jammedCockpitRefusal(mech, "mount");
  if (jammed) return err(jammed);
  const dist = combatantRange(state, pilotId, mechId);
  if (dist !== 1) return err(`mount: ${pilot.name} must be adjacent to ${mech.name} (got distance ${dist ?? "unreachable"})`);
  return ok(undefined);
}

/**
 * Mint the on-foot pilot {@link Combatant} a Dismount/Eject spawns — purely from the mech's
 * frozen `base.pilotBlock`, no roster/compendium read. Assumes legality already checked
 * ({@link checkDismountLegality}/{@link checkEjectLegality} guarantee `pilotBlock` is present);
 * placed directly at `at` (the room composes this into an `add_combatant`, never a follow-up
 * `move_token` — mirrors `wreckConversion.ts`'s directly-placed wreck).
 *
 * Mirrors {@link "./spawn" spawnPilot}'s on-foot restriction profile exactly: no heat
 * track, no consequence tracks, GRIT-derived save-bonus/attack/HASE-axes (`block.attackBonus` IS grit
 * — `ComputedPilotStats.attackBonus`'s own doc: "Equals grit"), Biological tech-immunity.
 */
export function spawnDismountedPilot(mech: Combatant, pilotId: CombatantId, at: Cell): Combatant {
  const block = mech.base.pilotBlock;
  if (!block) throw new Error(`spawnDismountedPilot: mech ${mech.id} carries no pilotBlock (legality should have rejected this)`);

  const base: CombatantBase = {
    maxHp: block.hp,
    // No heat/structure/stress track — a dismounted/ejected pilot on foot (mirrors
    // `spawn.ts`'s `spawnPilot`: `maxHeat`/`maxStructure`/`maxStress` simply omitted —
    // track presence IS the capability).
    armor: block.armor,
    evasion: block.evasion,
    edef: block.edef,
    speed: block.speed,
    size: block.size,
    // The `:3970` on-foot carve-out — GRIT on the save roll. `block.attackBonus` IS grit
    // (`ComputedPilotStats.attackBonus`'s own doc: "Equals grit"), the same source the three
    // axes below already read. Mirrors `spawn/pilot.ts` so both minting paths agree.
    saveBonus: block.attackBonus,
    // The DC this pilot imposes — `10 + grit`, a different number from `saveBonus` above
    // (`decisions/0163`).
    saveTarget: block.save,
    attackBonus: block.attackBonus,
    sensors: block.sensorRange,
    agility: block.attackBonus,
    systems: block.attackBonus,
    hull: block.attackBonus,
    // Mirrors `spawn.ts`'s `spawnPilot` — a dismounted/ejected pilot on foot at 0 HP goes
    // Down and Out, not destroyed outright.
    destructionOutcome: "downAndOut",
    // Mirrors `spawn.ts`'s `spawnPilot` — a pilot is always PC-side, regardless of
    // `controllerSlotId` (set just below from the mech's own, for ownership/visibility,
    // not side).
    allegiance: "pc",
  };
  if (block.weapons.length > 0) base.weapons = block.weapons;

  // A Sidearm-tagged gear weapon bumps the Fight once-per-turn allowance to 2 — mirrors
  // `spawn.ts`'s `spawnPilot` so both minting paths agree.
  const allowances = sidearmFightAllowance(block.weapons);
  if (allowances) base.actionAllowances = allowances;

  // Carry the pilot's gear-EFFECT ids across the mech→pilot boundary too — mirrors `spawn.ts`'s
  // `spawnPilot` so a Dismount/Eject-minted pilot gathers Frag Grenades'/Patch's/Corrective's/
  // Mobility Hardsuit's DSL clauses exactly like a direct GM-dropped pilot does.
  if (block.pilotGearEffectIds && block.pilotGearEffectIds.length > 0) base.pilotGearEffectIds = block.pilotGearEffectIds;

  // Carry the pilot's gear-granted composite action ids + usage caps across the same boundary —
  // mirrors `spawn.ts`'s `spawnPilot` so a Dismount/Eject-minted pilot's gear reaches the SAME
  // pressable, correctly-capped bar row a direct GM-dropped pilot gets. Without both this and the
  // effect ids above, the same kit's reach would depend on how the pilot got onto the map.
  // The affordance table crosses the same boundary as the two maps above, for the same reason
  // (`decisions/0176`): a dismounted pilot must carry the record table a directly-deployed one
  // carries, or the record's reach would depend on how the pilot got onto the map.
  if (block.affordances && block.affordances.length > 0) base.affordances = block.affordances;
  // The gear actions' reach crosses with them (Frag Grenades' Range 5), mirroring `spawnPilot`.
  if (block.actionRanges) base.actionRanges = block.actionRanges;

  // Mirrors `spawn.ts`'s `spawnPilot` — the on-foot pilot's CORE vocabulary is `PILOT_ACTIONS`,
  // stamped as `pilot:*` composites.
  base.coreActionIds = coreActionIdsFor("pilot");

  return {
    id: pilotId,
    kind: "pilot",
    name: block.callsign || block.name,
    base,
    // No `heat`/`structure`/`stress` — mirrors `base.maxHeat`/`maxStructure`/`maxStress`'s
    // omission.
    current: { hp: base.maxHp },
    statuses: [{ name: BIOLOGICAL_STATUS, expiry: { kind: "until_removed" } as const }],
    sourceRef: mech.pilotId,
    pos: at,
    // Snapshot at eject/dismount time, not a live reference — the same deliberate
    // non-cascade shape as the drone/mine deploy sites (`refereeDeployAction.ts`'s
    // header): reassigning the MECH's
    // controller after the pilot has already ejected/dismounted does NOT retroactively
    // move the now-independent on-foot pilot's controller.
    controllerSlotId: mech.controllerSlotId,
  };
}

/** The recorded step list for a Dismount's mech-side effect: land `Unpiloted`, partnered to the spawned pilot. */
export function dismountSteps(mechId: CombatantId, pilotId: CombatantId): ResolvedStep[] {
  return [{ op: "status_add", target: mechId, status: UNPILOTED_STATUS, expiry: { kind: "until_removed" }, partner: pilotId }];
}

/**
 * The recorded step list for an Eject's mech-side effect: `Unpiloted` (partnered) + `Impaired`
 * (RAW p.70) + the Eject-lockout resource set to `1` (locked out until Full Repair).
 */
export function ejectSteps(state: EncounterState, mechId: CombatantId, pilotId: CombatantId): ResolvedStep[] {
  const prevLockout = state.combatants[mechId]?.current.resources?.[EJECT_LOCKOUT_RESOURCE] ?? 0;
  return [
    { op: "status_add", target: mechId, status: UNPILOTED_STATUS, expiry: { kind: "until_removed" }, partner: pilotId },
    { op: "status_add", target: mechId, status: "Impaired", expiry: { kind: "until_removed" } },
    { op: "resource_set", target: mechId, key: EJECT_LOCKOUT_RESOURCE, val: 1, prev: prevLockout },
  ];
}

/**
 * The recorded step list for a Mount's mech-side effect: remove `Unpiloted`, carrying the LIVE
 * instance's `expiry`/`source`/`partner`/`cause` onto the step so undo/redo rebuilds it
 * byte-exact (mirrors `shutdown.ts`'s tech-caused-status-removal loop). Empty when the mech
 * isn't currently Unpiloted (the room's `checkMountLegality` already rejects that case; this is
 * belt-and-braces, like `bootUpSteps`' own idempotent-empty shape).
 */
export function mountSteps(state: EncounterState, mechId: CombatantId): ResolvedStep[] {
  const inst = state.combatants[mechId]?.statuses.find((s) => s.name === UNPILOTED_STATUS);
  if (!inst) return [];
  const step: Extract<ResolvedStep, { op: "status_remove" }> = {
    op: "status_remove",
    target: mechId,
    status: UNPILOTED_STATUS,
    ...(inst.expiry !== undefined ? { expiry: inst.expiry } : {}),
    ...(inst.appliedTurn !== undefined ? { appliedTurn: inst.appliedTurn } : {}),
    ...(inst.source !== undefined ? { source: inst.source } : {}),
    ...(inst.partner !== undefined ? { partner: inst.partner } : {}),
    ...(inst.cause !== undefined ? { cause: inst.cause } : {}),
  };
  return [step];
}

// ── the destroy-consequence extension ─────────────────────────────────────────────

/**
 * One mech's owed pilot-ejection consequence: the
 * mech was destroyed ORDINARILY (never vaporized — see {@link resolvePilotEjections}'s
 * doc), cockpit protection is lost, and its surviving pilot must become a targetable
 * on-foot combatant.
 */
export type PilotEjection = {
  /** The destroyed mech's id. */
  mechId: CombatantId;
  /** The minted on-foot pilot combatant, positioned adjacent to the wreck's footprint. */
  pilot: Combatant;
  /**
   * The double-activation guard: `true` iff the mech had ALREADY fully ended its turn
   * this round (`state.activation.activated`) at the moment it was destroyed — the
   * newly-spawned pilot inherits that acted-state so the pair doesn't get a bonus fresh
   * activation the same round. `false` when the mech is destroyed before ever taking a
   * turn this round (e.g. killed as a defender) — the pilot gets its own independent turn.
   * Unlike Dismount/Eject, which stamp UNCONDITIONALLY (using the bridge
   * action IS always the mech's own turn), destruction can strike at any point in the
   * round, so this is read off the live activation ledger instead of assumed.
   */
  stampActivated: boolean;
};

/**
 * Compute every pilot-ejection owed **right now**:
 * a placed `mech`-kind combatant carrying a frozen `pilotBlock` (every `spawnMech`
 * deploy freezes one) that is owed a **wreck** conversion ({@link
 * "./wreckConversion".destroyedConversionKind} `=== "wreck"` — an ordinary, non-vaporized
 * destroy) and hasn't already ejected. A **Vaporized** carrier ({@link
 * "./wreckConversion".destroyedConversionKind} `=== "unplace"`) is never owed a spawn — RAW
 * p.81: the pilot dies WITH the mech, no ejection, no wreck (the
 * meltdown-blast emission site, {@link "./meltdown".resolveMeltdownBlast}, already records
 * the death in its own warnings: "any pilot inside dies" — nothing new needed here).
 *
 * Idempotent: skips a mech whose deterministic {@link dismountedPilotIdFor} id already
 * names a live combatant (a repeat call in the same resolution, or a second observer pass,
 * is a no-op rather than double-spawning — mirrors {@link "./wreckConversion".
 * resolveWreckConversions}'s `pos !== null` idempotency marker, just keyed differently
 * since a mech doesn't get unplaced by its OWN pilot's ejection).
 *
 * **Positions the pilot on a free cell ADJACENT to the dead mech's own footprint** — NOT
 * the wreck's own cell. The wreck is a passable `object` (`blocksMovement: false` — a
 * passable object never blocks ENDING), but a `pilot`-kind combatant is an ordinary
 * occupant (only a MOVING mech gets the `"noStop"` pass-through; it still blocks another
 * token from ENDING its move there). Stacking the pilot directly onto the wreck's cell
 * would silently double-block it — reachable via the movement-budget proof
 * (`cli/proofs/mech-destruction.test.ts` scene 6), which relies on a mover being able to END
 * exactly on a bare wreck's cell.
 * Picks the FIRST neighbor (the world's own deterministic neighbor order) with a free
 * Size-0.5 footprint ({@link pilotFootprintFits}, the same gate Dismount/Eject's own
 * placement uses); falls back to the mech's own former cell only if every neighbor is
 * occupied (an automated GM consequence must still produce a targetable pilot even when
 * fully boxed in — a rare edge no player action can construct).
 *
 * This function must run off state captured BEFORE {@link "./wreckConversion".
 * resolveWreckConversions}'s unplace step clears `mech.pos` (the room's hand-chain method,
 * `spawnEjectedPilots`, is sequenced first for exactly this reason).
 *
 * Sorted by dead-mech id (deterministic, replay-stable — the same discipline {@link
 * "./wreckConversion".resolveWreckConversions}'s sweep uses).
 */
export function resolvePilotEjections(state: EncounterState): { ejections: PilotEjection[]; warnings: string[] } {
  const warnings: string[] = [];
  const ejections: PilotEjection[] = [];
  const mechs = Object.values(state.combatants)
    .filter((c) => c.kind === "mech" && c.pos != null && c.base.pilotBlock !== undefined && destroyedConversionKind(c) === "wreck")
    .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));

  for (const mech of mechs) {
    const pilotId = dismountedPilotIdFor(mech.id);
    if (state.combatants[pilotId]) continue; // already ejected — idempotent.
    const at = ejectionCellFor(state, mech.pos as Cell);
    const pilot = spawnDismountedPilot(mech, pilotId, at);
    const stampActivated = state.activation.activated.includes(mech.id);
    ejections.push({ mechId: mech.id, pilot, stampActivated });
    warnings.push(`${mech.name}: destroyed — cockpit protection lost (Core p.73), ${pilot.name} survives and stands beside the wreck.`);
  }
  return { ejections, warnings };
}
